import React from "react";

import clsx from "clsx";
import NumericInput from "react-numeric-input";

import { metrics } from "@north-beam/nb-common";

export interface ProviderWrapperProps {
  children: React.ReactElement | React.ReactElement[];
}

export const Blanket = (props: any) => (
  <div
    style={{
      bottom: 0,
      left: 0,
      top: 0,
      right: 0,
      position: "fixed",
      zIndex: props.zIndex ?? 1,
    }}
    {...props}
  />
);

export const H1 = ({
  children,
  ...rest
}: React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLHeadingElement>,
  HTMLHeadingElement
>) => (
  <h1 className={clsx(rest.className, "mb-3")} {...rest}>
    {children}
  </h1>
);

export const ScreenHeading = ({
  children,
  ...rest
}: React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLHeadingElement>,
  HTMLHeadingElement
>) => (
  <h1 className={clsx(rest.className, "mb-3 text-screen")} {...rest}>
    {children}
  </h1>
);

export function LightSwitch(props: {
  size?: "large" | "small" | "medium";
  isSet: boolean;
  disabled: boolean;
  onChange: (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => void | Promise<void>;
  id: string;
}) {
  const { isSet, onChange, id, disabled, size } = props;
  const htmlId = `check--${id}`;

  const sliderClasses = ["slider"];
  if (disabled) {
    sliderClasses.push("disabled");
  }

  return (
    <label className={`custom-switch-${size || "large"}`} htmlFor={htmlId}>
      <input
        className="hidden"
        disabled={disabled}
        type="checkbox"
        checked={isSet}
        onChange={onChange}
        id={htmlId}
      />
      <span className={clsx(sliderClasses)} />
    </label>
  );
}

export function InterleaveSpans(props: {
  text: React.ReactNode[];
  sep: React.ReactNode;
  Wrapper: any;
}) {
  const { text, sep, Wrapper } = props;
  const rv: React.ReactNode[] = [];
  for (let i = 0; i < text.length; ++i) {
    rv.push(<Wrapper key={`text-${i}`}>{text[i]}</Wrapper>);
    if (i < text.length - 1) {
      rv.push(sep);
    }
  }
  return <React.Fragment>{rv}</React.Fragment>;
}

export type ChangeOnBlurInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "onBlur"
>;

export function ChangeOnBlurInput({
  onChange,
  onKeyPress,
  value,
  ...rest
}: ChangeOnBlurInputProps) {
  if (value === null || typeof value === "undefined") {
    value = "";
  }

  const [tempValue, setTempValue] =
    React.useState<React.InputHTMLAttributes<HTMLInputElement>["value"]>(value);

  const ref = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    setTempValue(value);
    if (ref.current) {
      ref.current.focus();
    }
  }, [value]);

  const handleChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setTempValue(event.target.value);
    },
    [],
  );

  return (
    <input
      {...rest}
      ref={ref}
      value={tempValue}
      onChange={handleChange}
      onBlur={onChange}
      onKeyPress={(e) => {
        if (e.key === "Enter") {
          if (ref.current) {
            ref.current.blur();
            e.preventDefault();
          }
        } else {
          if (onKeyPress) {
            onKeyPress(e);
          }
        }
      }}
    />
  );
}

export function NumberInput({
  onValueChange,
  value,
  format,
  label,
}: {
  format: metrics.MetricType;
  onValueChange: (value: number | undefined) => void;
  value: number | undefined;
  label?: string;
} & Omit<ChangeOnBlurInputProps, "onChange" | "value">) {
  const [current, setCurrent] = React.useState(value);

  const onChange = React.useCallback(
    (num: number | null) => {
      if (num === null) {
        setCurrent(undefined);
        return;
      }

      if (format === "percentage") {
        num /= 100;
      }

      setCurrent(num);
    },
    [format, setCurrent],
  );

  const commit = React.useCallback(
    () => onValueChange(current),
    [onValueChange, current],
  );

  let pre: React.ReactNode = null;
  let post: React.ReactNode = null;
  let displayValue = current;
  if (format === metrics.MetricTypes.CURRENCY) {
    pre = (
      <div className="input-group-prepend h-8">
        <div className="input-group-text rounded-tr-none rounded-br-none">
          $
        </div>
      </div>
    );
  } else if (format === "multiplier") {
    post = (
      <div className="input-group-append h-8">
        <div className="input-group-text rounded-tl-none rounded-bl-none">
          ×
        </div>
      </div>
    );
  } else if (format === "percentage") {
    post = (
      <div className="input-group-append h-8">
        <div className="input-group-text rounded-tl-none rounded-bl-none">
          %
        </div>
      </div>
    );
    displayValue =
      typeof displayValue === "undefined" ? undefined : displayValue * 100;
  }
  const stringValue =
    typeof displayValue === "undefined" ? "" : String(displayValue);

  return (
    <div className="nb-input-container">
      {label && <label className="label">{label}</label>}
      <div className="flex">
        {pre}
        <NumericInput
          value={stringValue}
          className={clsx("nb-input flex-grow", {
            "rounded-tr-none rounded-br-none": post,
            "rounded-tl-none rounded-bl-none": pre,
          })}
          onChange={onChange}
          onBlur={commit}
          noStyle
        />
        {post}
      </div>
    </div>
  );
}

export function useInterval(callback: () => void, delay: number) {
  const savedCallback = React.useRef<() => void>();

  // Remember the latest callback.
  React.useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval.
  React.useEffect(() => {
    function tick() {
      savedCallback.current!();
    }
    if (delay !== null) {
      const id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
  }, [delay]);
}

export function Bubble(props: {
  color: string;
  title?: string;
  children: React.ReactNode | React.ReactNode[];
  borderRadius?: string;
}) {
  return (
    <div
      title={props.title}
      style={{
        fontSize: "12px",
        backgroundColor: props.color,
        color: "white",
        padding: "0px 8px",
        margin: "4px 4px",
        borderRadius: props.borderRadius ?? "0.75rem",
        display: "inline-block",
        lineHeight: "1.5rem",
      }}
    >
      {props.children}
    </div>
  );
}
